const TASK_STATUSES = ["todo", "in_progress", "done", "cancelled"];
const VIEWING_STATUSES = ["scheduled", "completed", "cancelled"];
const WORK_KINDS = ["task", "viewing"];

function lastAllowed(values, allowed) {
  const matched = (values || []).filter((v) => allowed.includes(v));
  if (matched.length === 0) return "";
  return matched[matched.length - 1];
}

function parsePositiveInt(raw) {
  if (raw == null || raw === "") return "";
  const n = Number.parseInt(String(raw), 10);
  return Number.isFinite(n) && n > 0 ? String(n) : "";
}

/**
 * Company work list URL state.
 * Backend status is singular — never send personal multi-status arrays.
 */
export function readWorkListQuery(search) {
  const params = new URLSearchParams(search || "");
  const pageRaw = Number.parseInt(params.get("page") || "1", 10);
  const page = Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1;
  const kind = lastAllowed(params.getAll("kind"), WORK_KINDS) || "task";
  const status = lastAllowed(
    params.getAll("status"),
    kind === "viewing" ? VIEWING_STATUSES : TASK_STATUSES,
  );
  const responsibleRaw = params.get("responsible_user") || "";
  const responsible_user =
    responsibleRaw === "unassigned"
      ? "unassigned"
      : parsePositiveInt(responsibleRaw);
  const due_after = params.get("due_after") || "";
  const due_before = params.get("due_before") || "";
  const scheduled_after = params.get("scheduled_after") || "";
  const scheduled_before = params.get("scheduled_before") || "";
  // I2 drill-down may use is_overdue=true; legacy toolbar uses overdue=1.
  const overdueRaw = params.get("is_overdue");
  const overdue =
    params.get("overdue") === "1" ||
    (overdueRaw != null &&
      String(overdueRaw).trim().toLowerCase() === "true");
  return {
    kind,
    status,
    responsible_user,
    client: parsePositiveInt(params.get("client")),
    property: parsePositiveInt(params.get("property")),
    overdue,
    due_after,
    due_before,
    scheduled_after,
    scheduled_before,
    create: lastAllowed(params.getAll("create"), WORK_KINDS),
    page,
  };
}

export function writeWorkListSearch(state) {
  const params = new URLSearchParams();
  const kind = WORK_KINDS.includes(state.kind) ? state.kind : "task";
  if (kind !== "task") params.set("kind", kind);
  const allowed =
    kind === "viewing" ? VIEWING_STATUSES : TASK_STATUSES;
  if (state.status && allowed.includes(state.status)) {
    params.set("status", state.status);
  }
  if (state.responsible_user === "unassigned") {
    params.set("responsible_user", "unassigned");
  } else if (state.responsible_user) {
    params.set("responsible_user", String(state.responsible_user));
  }
  if (state.client) params.set("client", String(state.client));
  if (state.property) params.set("property", String(state.property));
  if (kind === "task" && state.overdue) params.set("overdue", "1");
  if (kind === "task" && state.due_after) {
    params.set("due_after", String(state.due_after));
  }
  if (kind === "task" && state.due_before) {
    params.set("due_before", String(state.due_before));
  }
  if (kind === "viewing" && state.scheduled_after) {
    params.set("scheduled_after", String(state.scheduled_after));
  }
  if (kind === "viewing" && state.scheduled_before) {
    params.set("scheduled_before", String(state.scheduled_before));
  }
  if (state.create && WORK_KINDS.includes(state.create)) {
    params.set("create", state.create);
  }
  if (state.page > 1) params.set("page", String(state.page));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function buildTaskListParams(filters) {
  const params = {
    page: filters.page || 1,
    page_size: filters.pageSize || 20,
  };
  if (filters.status && TASK_STATUSES.includes(filters.status)) {
    params.status = filters.status;
  }
  if (filters.responsible_user) {
    params.responsible_user = filters.responsible_user;
  }
  if (filters.client) params.client = filters.client;
  if (filters.property) params.property = filters.property;
  if (filters.overdue) params.is_overdue = true;
  if (filters.due_after) params.due_after = filters.due_after;
  if (filters.due_before) params.due_before = filters.due_before;
  if (filters.ordering) params.ordering = filters.ordering;
  return params;
}

export function buildViewingListParams(filters) {
  const params = {
    page: filters.page || 1,
    page_size: filters.pageSize || 20,
  };
  if (filters.status && VIEWING_STATUSES.includes(filters.status)) {
    params.status = filters.status;
  }
  if (filters.responsible_user) {
    params.responsible_user = filters.responsible_user;
  }
  if (filters.client) params.client = filters.client;
  if (filters.property) params.property = filters.property;
  if (filters.scheduled_after) params.scheduled_after = filters.scheduled_after;
  if (filters.scheduled_before) {
    params.scheduled_before = filters.scheduled_before;
  }
  if (filters.ordering) params.ordering = filters.ordering;
  return params;
}

export function countSecondaryWorkFilters(filters) {
  let n = 0;
  if (filters.responsible_user) n += 1;
  if (filters.kind === "viewing") {
    if (filters.scheduled_after) n += 1;
    if (filters.scheduled_before) n += 1;
  } else {
    if (filters.due_after) n += 1;
    if (filters.due_before) n += 1;
    if (filters.overdue) n += 1;
  }
  return n;
}

export function secondaryWorkFilterSummary(filters) {
  const parts = [];
  if (filters.responsible_user) parts.push("Виконавець");
  if (filters.kind === "viewing") {
    if (filters.scheduled_after || filters.scheduled_before) {
      parts.push("Дати");
    }
  } else {
    if (filters.due_after || filters.due_before) parts.push("Дати");
    if (filters.overdue) parts.push("Прострочені");
  }
  return parts;
}

export function clearedToolbarWorkFilters(filters) {
  return {
    ...filters,
    status: "",
    responsible_user: "",
    overdue: false,
    due_after: "",
    due_before: "",
    scheduled_after: "",
    scheduled_before: "",
    page: 1,
  };
}

export { TASK_STATUSES, VIEWING_STATUSES, WORK_KINDS };
